import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { billingService, notificationService } from '../utils/api';
import {
  CreditCard,
  RefreshCw,
  ShieldCheck,
  Clock3,
  AlertTriangle,
  CheckCircle2,
  CalendarClock,
  ArrowRight,
  Sparkles,
  Bell,
  Mail,
  Send,
  RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusStyles = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  cancelled: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  expired: 'bg-gray-100 text-gray-700 dark:bg-dark-800 dark:text-gray-300',
  past_due: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  default: 'bg-gray-100 text-gray-700 dark:bg-dark-800 dark:text-gray-300'
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount, currency = 'INR') => {
  if (amount === undefined || amount === null) return '—';
  if (amount === 0) return 'Free';
  const symbol = currency === 'USD' ? '$' : '₹';
  return `${symbol}${amount}`;
};

const StatusPill = ({ status }) => {
  const style = statusStyles[status] || statusStyles.default;
  const label = status?.replace('_', ' ');
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${style}`}>
      {label || 'unknown'}
    </span>
  );
};

// Notification categories with descriptions
const notificationCategories = [
  {
    title: 'Account & Security',
    icon: ShieldCheck,
    settings: [
      { key: 'securityAlerts', label: 'Security Alerts', description: 'Critical security alerts and suspicious activity' },
      { key: 'apiKeyChanges', label: 'API Key Changes', description: 'New API keys created or revoked' },
      { key: 'loginAlerts', label: 'Login Notifications', description: 'Notifications when you sign in from a new device' }
    ]
  },
  {
    title: 'Billing & Subscription',
    icon: CreditCard,
    settings: [
      { key: 'billingUpdates', label: 'Billing Updates', description: 'Payment receipts and billing updates' },
      { key: 'subscriptionChanges', label: 'Subscription Changes', description: 'Plan upgrades, downgrades, and renewals' },
      { key: 'paymentFailed', label: 'Payment Failures', description: 'Failed payment attempts' }
    ]
  },
  {
    title: 'Usage & Limits',
    icon: Bell,
    settings: [
      { key: 'usageAlerts', label: 'Usage Alerts', description: 'When you reach 80% of your monthly limit' },
      { key: 'limitExceeded', label: 'Limit Exceeded', description: 'When you exceed your plan limits' }
    ]
  },
  {
    title: 'Product Updates',
    icon: Sparkles,
    settings: [
      { key: 'maintenanceAlerts', label: 'Maintenance Alerts', description: 'Scheduled maintenance and downtime notifications' }
    ]
  }
];

const SettingsPage = () => {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('subscription');
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const tabs = [
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'notifications', label: 'Email Preferences', icon: Bell }
  ];

  const usageProgress = useMemo(() => {
    if (!subscription?.limits) return 0;
    const { requestsUsed = 0, monthlyRequests = 0 } = subscription.limits;
    if (!monthlyRequests) return 0;
    return Math.min(100, Math.round((requestsUsed / monthlyRequests) * 100));
  }, [subscription]);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const requests = [
        billingService.getSubscription(),
        billingService.getPaymentHistory()
      ];

      if (activeTab === 'notifications') {
        requests.push(notificationService.getPreferences());
      }

      const results = await Promise.all(requests);
      
      setSubscription(results[0].data || null);
      setHistory(results[1].data?.history || []);
      
      if (results[2]) {
        setPreferences(results[2].data || null);
      }
    } catch (error) {
      console.error('Settings fetch error:', error);
      const message = error.response?.data?.error || 'Unable to load settings';
      toast.error(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const currentPlanLabel = useMemo(() => {
    if (!subscription?.plan) return '—';
    return subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1);
  }, [subscription]);

  const handleToggleNotification = async (key) => {
    if (!preferences) return;

    const newPreferences = {
      ...preferences,
      emailNotifications: {
        ...preferences.emailNotifications,
        [key]: !preferences.emailNotifications[key]
      }
    };

    setPreferences(newPreferences);

    try {
      await notificationService.updatePreferences({
        emailNotifications: {
          [key]: newPreferences.emailNotifications[key]
        }
      });
      toast.success('Preferences updated');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update preferences');
      setPreferences(preferences);
    }
  };

  const handleDigestChange = async (frequency) => {
    if (!preferences) return;

    const newPreferences = {
      ...preferences,
      digestFrequency: frequency
    };

    setPreferences(newPreferences);

    try {
      await notificationService.updatePreferences({ digestFrequency: frequency });
      toast.success('Digest frequency updated');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update digest frequency');
      setPreferences(preferences);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      await notificationService.sendTestEmail('welcome');
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      console.error('Test email error:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleResetPreferences = async () => {
    if (!confirm('Reset all email preferences to defaults?')) return;

    try {
      const { data } = await notificationService.resetPreferences();
      setPreferences(data.preferences);
      toast.success('Preferences reset to defaults');
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to reset preferences');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your subscription, notifications, and preferences.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              setRefreshing(true);
              try {
                if (activeTab === 'subscription') {
                  await fetchData();
                } else if (activeTab === 'notifications') {
                  const { data } = await notificationService.getPreferences();
                  setPreferences(data.preferences);
                }
                toast.success('Settings refreshed');
              } catch (error) {
                console.error('Refresh error:', error);
                toast.error('Failed to refresh settings');
              } finally {
                setRefreshing(false);
              }
            }}
            className="px-4 py-2 inline-flex items-center gap-2 rounded-lg bg-gray-100 dark:bg-dark-800 hover:bg-gray-200 dark:hover:bg-dark-700 transition"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">Refresh</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-800 overflow-x-auto">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-1 py-4 border-b-2 font-medium text-sm transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 card bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <ShieldCheck className="w-5 h-5 text-primary-500" />
                    <span className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400">Subscription Overview</span>
                  </div>
                  <h2 className="text-2xl font-bold">{currentPlanLabel} Plan</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {subscription?.billingPeriod === 'yearly' ? 'Yearly billing' : 'Monthly billing'} · {subscription?.currency || 'INR'}
                  </p>
                </div>
                <StatusPill status={subscription?.status} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Next renewal</p>
                  <p className="text-lg font-semibold">{formatDate(subscription?.currentPeriodEnd)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Auto renew {subscription?.autoRenew ? 'on' : 'off'}</p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Last payment</p>
                  <p className="text-lg font-semibold">{formatCurrency(subscription?.amountPaid, subscription?.currency)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Original: {formatCurrency(subscription?.originalAmount, subscription?.currency)}</p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Requests this cycle</p>
                  <p className="text-lg font-semibold">{subscription?.limits?.requestsUsed ?? 0} / {subscription?.limits?.monthlyRequests ?? '—'}</p>
                  <div className="mt-2 w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-primary-500"
                      style={{ width: `${usageProgress}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-primary-50 to-orange-50 dark:from-primary-900/20 dark:to-orange-900/10 border border-primary-100 dark:border-primary-900/30 flex items-center justify-between flex-col md:flex-row gap-3">
                <div className="flex items-center gap-3">
                  <CalendarClock className="w-5 h-5 text-primary-600" />
                  <div>
                    <p className="text-sm font-semibold">Need a different plan?</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Switch plans anytime—changes take effect immediately.</p>
                  </div>
                </div>
                <Link to="/pricing" className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold">
                  Manage plan <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="card bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Account</p>
                  <p className="text-lg font-semibold">Billing Contact</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-semibold">{user?.fullName || '—'}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{user?.primaryEmailAddress?.emailAddress || '—'}</p>
                <div className="mt-4 p-3 rounded-lg bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-sm text-gray-600 dark:text-gray-400">
                  Receipts and subscription updates will be sent to this email.
                </div>
              </div>
            </div>
          </div>

          {/* Payment History */}
          <div className="card bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Clock3 className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Payments</p>
                  <h3 className="text-xl font-semibold">History</h3>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Last {history.length || 0} records</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
            ) : history.length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                <p className="mb-2">No payments yet.</p>
                <Link to="/pricing" className="text-primary-600 dark:text-primary-400 font-semibold inline-flex items-center gap-2">
                  Choose a plan <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-dark-800">
                      <th className="py-3 pr-4">Plan</th>
                      <th className="py-3 pr-4">Status</th>
                      <th className="py-3 pr-4">Amount</th>
                      <th className="py-3 pr-4">Cycle</th>
                      <th className="py-3 pr-4">Payment ID</th>
                      <th className="py-3 pr-4">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-dark-800">
                    {history.map((item) => {
                      const StatusIcon = item.status === 'active' ? CheckCircle2 : item.status === 'past_due' ? AlertTriangle : Clock3;
                      return (
                        <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-dark-800/60 transition-colors">
                          <td className="py-4 pr-4">
                            <div className="font-semibold capitalize">{item.plan}</div>
                            <div className="text-xs text-gray-500">{item.currency}</div>
                          </td>
                          <td className="py-4 pr-4">
                            <div className="flex items-center gap-2">
                              <StatusIcon className="w-4 h-4" />
                              <StatusPill status={item.status} />
                            </div>
                          </td>
                          <td className="py-4 pr-4">
                            <div className="font-semibold">{formatCurrency(item.amountPaid, item.currency)}</div>
                            {item.discount ? (
                              <div className="text-xs text-green-600 dark:text-green-400">Saved {formatCurrency(item.discount, item.currency)}</div>
                            ) : null}
                          </td>
                          <td className="py-4 pr-4">
                            <div className="capitalize">{item.billingPeriod || '—'}</div>
                            <div className="text-xs text-gray-500">Ends {formatDate(item.currentPeriodEnd)}</div>
                          </td>
                          <td className="py-4 pr-4 text-xs text-gray-600 dark:text-gray-400">{item.paymentId || '—'}</td>
                          <td className="py-4 pr-4 text-xs text-gray-600 dark:text-gray-400">{formatDate(item.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Preferences Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          <div className="card bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-5 h-5 text-primary-500" />
                  <h2 className="text-xl font-bold">Email Notifications</h2>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Control which emails you receive. All emails sent to <strong>{user?.primaryEmailAddress?.emailAddress}</strong>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSendTestEmail}
                  disabled={sendingTest}
                  className="px-4 py-2 inline-flex items-center gap-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition disabled:opacity-50 text-sm"
                >
                  <Send className={`w-4 h-4 ${sendingTest ? 'animate-pulse' : ''}`} />
                  Test Email
                </button>
                <button
                  onClick={handleResetPreferences}
                  className="px-4 py-2 inline-flex items-center gap-2 rounded-lg bg-gray-100 dark:bg-dark-800 hover:bg-gray-200 dark:hover:bg-dark-700 transition text-sm"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700">
              <label className="block text-sm font-semibold mb-3">Email Frequency</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {['instant', 'daily', 'weekly', 'never'].map((freq) => (
                  <button
                    key={freq}
                    onClick={() => handleDigestChange(freq)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition ${
                      preferences?.digestFrequency === freq
                        ? 'bg-primary-600 text-white'
                        : 'bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 hover:border-primary-300'
                    }`}
                  >
                    {freq}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {preferences?.digestFrequency === 'instant' && 'Receive emails immediately'}
                {preferences?.digestFrequency === 'daily' && 'Receive a daily digest of notifications'}
                {preferences?.digestFrequency === 'weekly' && 'Receive a weekly digest of notifications'}
                {preferences?.digestFrequency === 'never' && 'Disable non-critical email notifications'}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="card flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              Loading preferences...
            </div>
          ) : (
            notificationCategories.map((category) => {
              const Icon = category.icon;
              return (
                <div key={category.title} className="card bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className="w-5 h-5 text-primary-500" />
                    <h3 className="text-lg font-semibold">{category.title}</h3>
                  </div>
                  <div className="space-y-4">
                    {category.settings.map((setting) => (
                      <div key={setting.key} className="flex items-start justify-between py-3 border-b border-gray-100 dark:border-dark-800 last:border-0">
                        <div className="flex-1 pr-4">
                          <label htmlFor={setting.key} className="font-medium cursor-pointer">
                            {setting.label}
                          </label>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {setting.description}
                          </p>
                        </div>
                        <button
                          id={setting.key}
                          onClick={() => handleToggleNotification(setting.key)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            preferences?.emailNotifications?.[setting.key]
                              ? 'bg-primary-600'
                              : 'bg-gray-200 dark:bg-dark-700'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              preferences?.emailNotifications?.[setting.key]
                                ? 'translate-x-5'
                                : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
